import { For, Show, createSignal, type Component } from 'solid-js';

import { enterZone, isReturnGate, returnToHub } from '../net/protocol';
import type { GameStore } from '../state/store';
import { lootLabelFor } from './lootVisibility';

/**
 * Enemy levels offered relative to the suggestion, per the post-map prompt in
 * `doc/grind/design/HUB-ZONES-SCRAP-UI.md`.
 */
const OFFSETS = [-2, 0, 1, 3] as const;

/** What to suggest before the CHARACTER frame says how strong you are. */
export const FALLBACK_LEVEL = 1;

/**
 * The suggested enemy level: your own level.
 *
 * The spec derives this from character level *and clear speed*. Levels exist now,
 * so this reads the real one — but nothing measures how fast a zone gets cleared,
 * so the other half of the formula is still missing and the suggestion is not yet
 * adaptive. Matching the player's level is the honest version of that: an even
 * fight, with the offsets either side for players who want otherwise.
 */
export function suggestedLevel(store: GameStore): number {
  return store.progression()?.level ?? FALLBACK_LEVEL;
}

/**
 * Clamps a level into something the server will accept.
 *
 * The floor matters at level 1 and 2, where the -2 button would otherwise ask for
 * a level below the first one — a request the server would reject, leaving a
 * button that silently does nothing.
 */
export function levelFor(offset: number, suggested = FALLBACK_LEVEL): number {
  return Math.max(1, suggested + offset);
}

/** How the offset reads on the button. */
export function offsetLabel(offset: number): string {
  if (offset === 0) return 'Current';
  return offset > 0 ? `+${offset}` : String(offset);
}

/**
 * The highest enemy level a zone can be asked for.
 *
 * `game.MaxLevel` in Go, and the clamp there is the one that decides — this is the same
 * number so the input cannot offer a value the server will quietly reduce. A field that
 * accepts 500 and hands back a level-100 zone is worse than one that refuses 500.
 */
export const MAX_ENEMY_LEVEL = 100;

/**
 * The typed level, clamped into what the server accepts.
 *
 * Exact levels exist because the four offsets could not express *"give me something well
 * under my level"* or *"far over it"* — owner's call, 2026-08-11. The presets stay: they are
 * the common cases and one press, and this is the override.
 *
 * A blank or unparseable box is **not** a level. It returns null rather than a default, so
 * *Descend* can refuse instead of silently diving at level 1 — the same rule as an action-bar
 * slot with nothing in it.
 */
export function exactLevel(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  // Rejected rather than truncated: `Number` accepts `1e3` and `12.7`, and a player who typed
  // one of those did not ask for the level they would silently get.
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (value < 1) return null;
  return Math.min(value, MAX_ENEMY_LEVEL);
}

/**
 * The one-click GRIND portal, and the way back.
 *
 * Only rendered in town: `store.inHub()` comes from the zone's kind, so the
 * server decides where the portal exists rather than the client guessing.
 *
 * The two halves are not symmetrical, and that is the design. Out of town is a
 * *click* — the level select is a choice, and there is nothing to walk to yet. Back
 * into town is a **place**: the button opens a gate beside the player and walking
 * into it is what travels, checked server-side at the end of `Step` exactly as the
 * zone's exit is.
 */
export const GrindPortal: Component<{
  store: GameStore;
  /**
   * Walks to the return gate and steps through, when there is one.
   *
   * Passed in rather than reached for, because walking is the `Navigator`'s and this is a
   * HUD component: a panel that owned a route would be a second thing steering the player.
   * `Hud` wires it to the same walk-then-use a click on the gate performs.
   */
  onWalkToGate?: (gate: { x: number; z: number }) => void;
}> = (props) => {
  // The open state lives in the store, not here. The **The Grind** waypoint in town
  // opens the same dialog, and a modal covering the viewport has to suspend world
  // input — see the note on `MenuPanel`.
  const open = () => props.store.menu() === 'portal';
  const suggested = () => suggestedLevel(props.store);
  // The suspended run, if there is one. In town the only gate that can be standing
  // is the return gate, but this asks by kind rather than by count: a portal is
  // `TOWN` by default on the wire, so counting would call a missing field a run.
  const held = () => props.store.portals().find(isReturnGate);

  const enter = (offset: number) => {
    props.store.send(enterZone(levelFor(offset, suggested())));
    props.store.closeMenu();
  };

  // The typed override. Empty until somebody types, so the presets stay the default path and
  // the box reads as an override rather than a required field.
  const [typed, setTyped] = createSignal('');
  const typedLevel = () => exactLevel(typed());

  const diveExact = () => {
    const level = typedLevel();
    // Never `disabled`: the same rule the action bar follows. A button that vanishes as you
    // clear the box is a button you cannot find again.
    if (level === null) return;
    props.store.send(enterZone(level));
    props.store.closeMenu();
  };

  return (
    <>

      {/*
        **In town, the way back is the town bar's, not this component's.** `[ GRIND ]` and
        *Back to the Run* both used to be floating sigils here; they are two of the three
        categories in `TownBar` now, because a dive and a resumed run are places you go and
        the bar is the list of those. What is left below is the one thing that is *not* a
        place: opening a gate home from inside a zone.

        The way back **opens a portal**; it does not travel. The label says so, because a
        button that used to teleport and now asks you to walk five units would otherwise
        read as broken for exactly as long as it takes to notice the gate beside you. It
        stays enabled while one is already open: the press re-places it, and the server's
        half-second guard is what stops a double press doing that under a player already
        walking in.
      */}
      <Show when={!props.store.inHub()}>
        <button
          type="button"
          class="sigil-button grind-return hud-interactive"
          data-testid="return-button"
          onClick={() => props.store.send(returnToHub())}
        >
          <span>{props.store.portals().length > 0 ? 'Portal Open' : 'Open Town Portal'}</span>
        </button>
      </Show>

      <Show when={open()}>
        <div class="level-select" data-testid="level-select">
          <div class="plate plate--riveted level-select__panel hud-interactive">
            <h2 class="level-select__title">Choose your Depth</h2>
            <p class="level-select__lede">
              The biome is rolled for you. Only the enemy level is yours to pick — how it looks and
              how hard it hits are decided separately.
            </p>
            <hr class="rule" />

            {/* A dive throws away a suspended run, and the player has to be told
                *before* they press it — the return gate is at the other end of the
                street, so it is perfectly possible to open this dialog having
                forgotten it is there. Derived from the gate the server is already
                sending rather than from a flag of its own: if there is no gate on
                screen there is nothing to abandon. */}
            <Show when={held()}>
              {(gate) => (
                <p class="level-select__warning" data-testid="abandon-warning">
                  A run is still open at Depth {gate().level ?? '?'}. Diving abandons it — the
                  return gate is the only way back.
                </p>
              )}
            </Show>

            <div class="level-select__options">
              <For each={OFFSETS}>
                {(offset) => (
                  <button
                    type="button"
                    class="sigil-button level-select__option"
                    classList={{ 'level-select__option--current': offset === 0 }}
                    data-testid={`level-${offset}`}
                    onClick={() => enter(offset)}
                  >
                    <span>
                      <span class="level-select__offset">{offsetLabel(offset)}</span>
                      <span class="level-select__level">Lv {levelFor(offset, suggested())}</span>
                    </span>
                  </button>
                )}
              </For>
            </div>

            {/* The override. Its own row under the presets, because it is the same choice
                made precisely rather than a fifth preset — and `Enter` dives, since a player
                who has just typed a number is already at the keyboard. */}
            <div class="level-select__exact">
              <label class="level-select__exact-field">
                <span class="level-select__exact-label">Exact level</span>
                <input
                  type="number"
                  class="level-select__exact-input"
                  data-testid="level-exact"
                  min="1"
                  max={MAX_ENEMY_LEVEL}
                  placeholder={String(suggested())}
                  value={typed()}
                  onInput={(e) => setTyped(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') diveExact();
                  }}
                />
              </label>
              <button
                type="button"
                class="sigil-button level-select__exact-go"
                data-testid="level-exact-go"
                onClick={diveExact}
              >
                <span>{typedLevel() === null ? 'Descend' : `Descend at Lv ${typedLevel()}`}</span>
              </button>
            </div>

            <p class="level-select__note">
              Current matches your own level, Lv {suggested()}. Nothing measures how fast you clear a
              zone yet, so the suggestion does not adapt beyond that. Anything from 1 to{' '}
              {MAX_ENEMY_LEVEL} can be typed instead — the loot scales with the enemies, and so does
              what they do to you.
            </p>

            <button
              type="button"
              class="sigil-button level-select__cancel"
              data-testid="level-cancel"
              onClick={() => props.store.closeMenu()}
            >
              <span>Not yet</span>
            </button>
          </div>
        </div>
      </Show>
    </>
  );
};

/**
 * A prompt over the nearest drop, so a player knows loot is collectable rather than
 * scenery.
 *
 * **It does not repeat a name the label is already showing.** `LootLabels` draws every
 * visible drop's name, styled by the loot filter, and this prompt hangs off the *same*
 * anchor — so the nearest drop had its name rendered twice, in two boxes 34px apart, and
 * read as the item being listed twice. Reported by the owner as *"the drops are displayed
 * twice"*.
 *
 * **Asked through `lootLabelFor`, which is the whole rule.** This first shipped asking the loot
 * *filter* whether the drop was labelled — one of the rule's five inputs. Holding **Alt** labels a
 * drop the filter hid, so the prompt still printed the name and the duplicate came straight back:
 * *"the item labels on the ground are duplicated again when hitting alt"*. A freshly-landed drop
 * inside its impulse window did the same with no key held.
 *
 * The prompt's job is the **affordance**, not the identity: the label says what it is and
 * this says you can take it. So the name appears here only when the filter has *hidden*
 * the label — a `Hide` rule leaves the item on the floor and collectable, and the one drop
 * a player is standing on must still be identifiable even when their own filter says not
 * to shout about it.
 */
export const LootPrompt: Component<{ store: GameStore }> = (props) => (
  <Show when={props.store.nearestLoot()}>
    {(loot) => {
      const anchor = () => props.store.anchors()[loot().id];
      /** True when `LootLabels` is already naming this drop — the same question it asks. */
      const labelled = () => lootLabelFor(props.store, loot()).showLabel;
      return (
        <Show when={anchor()?.visible}>
          <div
            class={`loot-prompt loot-prompt--${loot().rarity.toLowerCase()}`}
            classList={{ 'loot-prompt--hint-only': labelled() }}
            data-testid="loot-prompt"
            style={{ left: `${anchor()?.x ?? 0}px`, top: `${anchor()?.y ?? 0}px` }}
          >
            {/*
              **No *click to take* hint.**

              It was there because clicking a drop from across the room did nothing — the
              command was refused for range — so the prompt was really saying *stand on this
              and click*. A click now walks the player over and picks it up on arrival, which
              is what everybody expected the first time, and a label telling them to click
              the thing they are already looking at is noise over the world.
            */}
            <Show when={!labelled()}>
              <span class="loot-prompt__name">{loot().name}</span>
            </Show>
          </div>
        </Show>
      );
    }}
  </Show>
);
